import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import GreetingHero from '../../components/Dashboard/GreetingHero';
import KPIGrid from '../../components/Dashboard/KPIGrid';
import RevenueExpenseChart from '../../components/Dashboard/RevenueExpenseChart';
import CategoryChart from '../../components/Dashboard/CategoryChart';
import IncomeStatementSection from '../../components/Dashboard/IncomeStatementSection';
import QuarterlyAnalysisSection from '../../components/Dashboard/QuarterlyAnalysisSection';
import CategoryAnalysisSection from '../../components/Dashboard/CategoryAnalysisSection';
import GrowthMetricsSection from '../../components/Dashboard/GrowthMetricsSection';
import MerchantAnalysisSection from '../../components/Dashboard/MerchantAnalysisSection';
import InsightsCard from '../../components/Dashboard/InsightsCard';
import LoanReadinessCard from '../../components/Dashboard/LoanReadinessCard';
import CashflowCalendarCard from '../../components/Dashboard/CashflowCalendarCard';
import { useAccountBookStore } from '@/store/accountBookStore';
import PageSkeleton from '../../components/PageSkeleton';

// 상세 분석 섹션 — 탭으로 묶어 세로 스크롤 부담을 줄인다 (활성 탭만 렌더)
const DETAIL_TABS: Array<{ label: string; render: () => React.ReactNode }> = [
  { label: '손익계산서', render: () => <IncomeStatementSection /> },
  { label: '분기별 분석', render: () => <QuarterlyAnalysisSection /> },
  { label: '카테고리 분석', render: () => <CategoryAnalysisSection /> },
  { label: '성장 지표', render: () => <GrowthMetricsSection /> },
  { label: '거래처 분석', render: () => <MerchantAnalysisSection /> },
];

const Dashboard: React.FC = () => {
  const {
    isInitialized,
    isLoading,
    dashboardData,
    currentAccountNumber,
    userId,
    fetchDashboardData,
  } = useAccountBookStore();

  const [detailTab, setDetailTab] = useState(0);

  // currentAccountNumber 변경 시 자동으로 데이터 다시 로드
  useEffect(() => {
    if (!isInitialized || !userId || isLoading) {
      return;
    }
    fetchDashboardData(dashboardData.selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccountNumber, isInitialized, userId]);

  // 캐시된 데이터가 있으면 즉시 렌더 (stale-while-revalidate) — 풀스크린 로딩은 첫 진입 때만
  const hasCachedData =
    dashboardData.monthlyData.length > 0 || dashboardData.kpiData.recordCount > 0;
  const isRefreshing = !isInitialized || isLoading;

  // 첫 진입(캐시 없음)에도 화면 골격을 먼저 그린다 — 데이터는 백그라운드에서 채움
  if (isRefreshing && !hasCachedData) {
    return <PageSkeleton blocks={[140, 360, 460]} />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: 'transparent',
        color: 'var(--fg-default)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        padding: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* 백그라운드 갱신 표시 — 캐시 데이터 위에 얇은 진행바만 */}
      {isRefreshing && hasCachedData && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            height: 3,
            '& .MuiLinearProgress-bar': { backgroundColor: 'var(--primary)' },
            backgroundColor: 'var(--primary-soft)',
          }}
        />
      )}

      {/* 인사 카드 — 최상단 */}
      <GreetingHero />

      {/* 쉬운 말 인사이트 — 인사말 바로 아래 최상단 노출 */}
      <InsightsCard />

      {/* KPI 카드 그리드 */}
      <KPIGrid />

      {/* 대출 준비도 — 2026 SCB(소상공인 특화 신용평가) 대비 참고 지표 */}
      <LoanReadinessCard />

      {/* 차트 섹션 */}
      <Grid container spacing={2}>
        {/* 매출/지출 트렌드 차트 */}
        <Grid item xs={12} lg={8}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              height: { xs: '400px', sm: '450px', md: '500px' },
              background: 'var(--bg-base)',
              borderRadius: 'var(--r-20)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <RevenueExpenseChart />
          </Paper>
        </Grid>

        {/* 카테고리별 지출 파이 차트 */}
        <Grid item xs={12} lg={4}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              height: { xs: '400px', sm: '450px', md: '500px' },
              background: 'var(--bg-base)',
              borderRadius: 'var(--r-20)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <CategoryChart />
          </Paper>
        </Grid>
      </Grid>

      {/* 자금 흐름 캘린더 (월 단위, 자체 상태) */}
      <CashflowCalendarCard />

      {/* 상세 분석 — 탭 */}
      <Box>
        {/* 필 세그먼트 탭 — 선택 탭은 솔리드 그린 필 (Quixotic 상단 내비 패턴) */}
        <Paper
          elevation={0}
          sx={{
            background: 'var(--bg-base)',
            borderRadius: 'var(--r-pill)',
            border: '1px solid var(--border-subtle)',
            mb: 2,
            display: 'inline-block',
            maxWidth: '100%',
          }}
        >
          <Tabs
            value={detailTab}
            onChange={(_, v) => setDetailTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              px: 1,
              minHeight: 56,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                fontSize: 16,
                fontWeight: 600,
                minHeight: 40,
                my: 1,
                mx: 0.25,
                px: 2.5,
                borderRadius: 'var(--r-pill)',
                color: 'var(--fg-secondary)',
                transition: 'all var(--motion-fast)',
                '&.Mui-selected': {
                  backgroundColor: 'var(--primary)',
                  color: 'var(--fg-on-primary)',
                },
                '&:hover:not(.Mui-selected)': {
                  backgroundColor: 'var(--bg-muted)',
                },
              },
            }}
          >
            {DETAIL_TABS.map((tab) => (
              <Tab key={tab.label} label={tab.label} />
            ))}
          </Tabs>
        </Paper>

        {DETAIL_TABS[detailTab].render()}
      </Box>
    </Box>
  );
};

export default Dashboard;
